'use client';

import { FC, useEffect, useRef } from 'react';
import { PrismicNextImage } from '@prismicio/next';
import { PrismicLink, PrismicRichText } from '@prismicio/react';
import { TestimonialCarousel2SliceWithoutFiltersPrimaryCardsItem } from '../../../../prismicio-types';
import { RichTextField } from '@prismicio/client';
import Link from 'next/link';

interface WithoutFiltersProps {
  title: RichTextField;
  caseStudies: Array<TestimonialCarousel2SliceWithoutFiltersPrimaryCardsItem>;
  slice: {
    slice_type: string;
    variation: string;
  };
}

const WithoutFilters: FC<WithoutFiltersProps> = ({
  title,
  caseStudies,
  slice,
}) => {
  const titleRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -10% 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in');
          entry.target.classList.remove('opacity-0', 'translate-y-8');
        }
      });
    }, observerOptions);

    const titleElement = titleRef.current;
    const gridElement = gridRef.current;

    if (titleElement) {
      titleElement.style.transitionDelay = '0ms';
      observer.observe(titleElement);
    }
    if (gridElement) {
      gridElement.style.transitionDelay = '150ms';
      observer.observe(gridElement);
    }

    return () => {
      if (titleElement) {
        observer.unobserve(titleElement);
      }
      if (gridElement) {
        observer.unobserve(gridElement);
      }
    };
  }, []);

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="bg-[#0e1110] py-20"
    >
      <div className="container mx-auto px-5 md:px-10 lg:px-[200px]">
        {/* Section Title */}
        <div
          ref={titleRef}
          className="text-center mb-6 opacity-0 translate-y-8 transition-all duration-700 ease-out"
        >
          <div className="text-white text-[30px] font-medium leading-[1.2] capitalize">
            <PrismicRichText field={title} />
          </div>
        </div>

        {/* Cards Grid */}
        <div
          ref={gridRef}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 opacity-0 translate-y-8 transition-all duration-700 ease-out"
        >
          {caseStudies.map((caseStudy, index) => (
            <CaseStudyCard key={index} caseStudy={caseStudy} />
          ))}
        </div>
      </div>
    </section>
  );
};

interface CaseStudyCardProps {
  caseStudy: TestimonialCarousel2SliceWithoutFiltersPrimaryCardsItem;
}

const CaseStudyCard: FC<CaseStudyCardProps> = ({ caseStudy }) => {
  const { industry, service, image, title, link } = caseStudy;

  return (
    <PrismicLink
      field={link}
      internalComponent={Link}
      className="rounded-[24px] overflow-hidden flex flex-col cursor-pointer group"
      style={{
        background:
          'radial-gradient(1786.22% 95.9% at 95.9% 0%, rgba(255, 255, 255, 0.08) 0%, rgba(3, 191, 158, 0.15) 46.67%, rgba(255, 255, 255, 0.15) 100%)',
      }}
    >
      {/* Content Section */}
      <div className="p-6 flex flex-col gap-4 h-[230px]">
        <span className="text-[#05f273] font-medium text-xs leading-[1.5] uppercase">
          {industry}
        </span>
        <div className="flex-1 text-white text-xl leading-[1.4] overflow-hidden">
          <PrismicRichText field={title} />
        </div>
        <span className="text-[#05f273] font-medium text-xs leading-[1.5] uppercase">
          {service}
        </span>
      </div>

      {/* Image Section with Logo Overlay */}
      <div className="relative h-[140px] w-full">
        <PrismicNextImage
          field={image}
          className="w-full h-full object-cover"
          alt=""
        />
        <div className="absolute inset-0 bg-black/40" />
      </div>
    </PrismicLink>
  );
};

export default WithoutFilters;
